/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { UserManagementListViewProps } from "./user-management-list-view.props";
import {
  AlertComponent,
  CountryCodeWithPhoneNumber,
  SelectInputComponent,
} from "../../../../common/components";
import "./user-management-list-view.scss";
import { AlertMessage } from "../../../tenant/tenant-details/tenant-details-types";
import { updateTenetUsers } from "../../../../api/tenets";
import { useLogin } from "../../../../common/hooks/useUser";
import {
  updateProfileUsers,
  updateTenantUsers,
} from "../../../../api/settings";
import CompanySelect from "../components/company-select/company-select";
import RoleSelect from "../components/role-select/role-select";
import { Typography } from "@mui/material";
import { AntSwitch } from "../components/ant-switch";
import phoneValidation from "../../../../common/components/phone-number-input-component/phone-validation";
import {
  getCurrentTenantId,
  isOnlyCountryCode,
  isValidPhoneNumber,
} from "../../../../utils/utils";
import { useResourcePermissions } from "../../../../common/hooks/resource-permissions";
import MODULES from "../../../../constants/modules";

const validationSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: Yup.string().test(
    "valid-phone-check",
    "Please enter a valid phone number",
    function (value) {
      if (!value) return false;

      if (isOnlyCountryCode(value)) {
        return this.createError({
          message: "Phone number is required",
        });
      }

      if (!phoneValidation(value)) {
        return this.createError({
          message: "Please enter a valid phone number",
        });
      }

      return true;
    }
  ),
  company_id: Yup.number().required("Company is required"),
  role: Yup.number().required("Role is required"),
  status: Yup.string().oneOf(["ACTIVE", "INACTIVE"]),
});

const UserManagementListView: React.FC<UserManagementListViewProps> = (
  props: any
) => {
  const {
    setIsViewEditOpen,
    setViewOpen,
    selectedUserDetails,
    refreshUsersList,
    companyNames,
    roles,
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const tenantDetails = useLogin();

  const handleEditOpen = () => setIsEditing(true);
  const { canWrite } = useResourcePermissions(MODULES.USER_LISTS);

  const tenatName = tenantDetails?.tenantDetails?.companyName;
  const tenantId = getCurrentTenantId();

  const initialValues = {
    first_name: selectedUserDetails?.first_name || "",
    last_name: selectedUserDetails?.last_name || "",
    email: selectedUserDetails?.email || "",
    phoneNumber: selectedUserDetails?.phone_number || "",
    company_id: selectedUserDetails?.tenantId ?? Number(tenantId),
    role: selectedUserDetails?.role?.value || "",
    role_name: selectedUserDetails?.role?.label || "",
    company_name: selectedUserDetails?.companyName ?? tenatName,
    company: {
      id: selectedUserDetails?.tenantId ?? Number(tenantId),
      company_name: selectedUserDetails?.companyName ?? tenatName,
    },
    status: selectedUserDetails?.status || "",
  };
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const handleSubmit = async (data: any, { resetForm, setFieldValue }: any) => {
    try {
      setLoading(true);
      const payload = {
        users: [
          {
            id: selectedUserDetails.id,
            firstName: data?.first_name,
            lastName: data?.last_name,
            email: data?.email,
            phoneNumber:
              data?.phoneNumber?.trim() === "" ? null : data?.phoneNumber,
            status: data?.status,
            roleId: data?.role,
            isPrimaryUser: data?.role === 1 ? true : false,
            isUpdate: true,
          },
        ],
      };
      const tenantCurrentId = parseInt(
        selectedUserDetails?.tenantId ?? Number(tenantId)
      );

      if (isSuperParter) {
        await updateTenetUsers(tenantCurrentId, payload);
      } else {
        await updateTenantUsers(tenantCurrentId, payload);
      }
      const selectedRoleLabel = roles.find(
        (r: any) => r.value === data.role
      )?.label;
      setFieldValue("role_name", selectedRoleLabel);
      setIsEditing(false);
      setAlertMessage({
        open: true,
        message: "User details updated successfully.",
        type: "success",
      });
    } catch (err: any) {
      console.error("Error updating tenant user details:", err);
      const errMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong!";
      setAlertMessage({
        open: true,
        message: errMessage,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const labelSwitch = { inputProps: { "aria-label": "Switch demo" } };

  return (
    <div className="user-management-list-view-info">
      <div className="user-management-list-view-info-header">
        <div className="user-management-list-view-info-header-text">
          User Details
        </div>
      </div>

      {!isEditing && canWrite && (
        <div className="user-management-list-view-info-edit-button-section">
          <div className="user-management-list-view-info-edit-button">
            {selectedUserDetails?.is_primary_user ? (
              <></>
            ) : (
              <button onClick={handleEditOpen}>Edit</button>
            )}
          </div>
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          touched,
          errors,
          resetForm,
        }) => {
          return (
            <Form className="user-management-list-view-info-data">
              <div className="user-management-list-view-info-data-right">
                <div className="user-management-list-view-info-data-right-label-text">
                  <div className="user-management-list-view-info-data-right-label">
                    Company
                  </div>
                  <div className="user-management-list-view-info-data-right-text">
                    {isEditing ? (
                      <>
                        <CompanySelect
                          value={values.company}
                          onChange={(option) =>
                            setFieldValue("company_id", option)
                          }
                          companyNames={companyNames}
                          disabled={true}
                          touched={!!touched.company_id}
                          error={errors.company_id}
                        />
                      </>
                    ) : (
                      values.company_name || "-"
                    )}
                  </div>
                </div>

                <div className="user-management-list-view-info-data-right-label-text">
                  <div
                    className={`user-management-list-view-info-data-right-label ${isEditing ? "disable-label" : ""}`}
                  >
                    Last Name
                  </div>
                  <div className="user-management-list-view-info-data-right-text">
                    {isEditing ? (
                      <>
                        <div className="user-management-edit-first-name">
                          {values.last_name || "-"}
                        </div>
                      </>
                    ) : (
                      values.last_name || "-"
                    )}
                  </div>
                </div>

                <div className="user-management-list-view-info-data-right-label-text">
                  <div className="user-management-list-view-info-data-right-label">
                    Phone Number
                  </div>
                  <div className="user-management-list-view-info-data-right-text phone-number">
                    {isEditing ? (
                      <div>
                        <CountryCodeWithPhoneNumber
                          fieldName="phoneNumber"
                          placeholder="Enter phone number"
                          onChange={(value: string, country: any) => {
                            const countryCode = country?.dialCode;

                            if (!countryCode) {
                              console.warn(
                                "Missing dialCode in country object"
                              );
                              return;
                            }

                            const numberOnly = value.slice(countryCode.length + 1);
                            const formatted = `+${countryCode} ${numberOnly}`;

                            setFieldValue("phoneNumber", formatted);
                          }}
                          onBlur={() => {
                            setFieldTouched("phoneNumber", true);
                          }}
                          value={values?.phoneNumber || ""}
                          defaultCountry="us"
                        />
                        {touched.phoneNumber &&
                          typeof errors.phoneNumber === "string" && (
                            <div className="error-message">
                              {errors.phoneNumber}
                            </div>
                          )}
                      </div>
                    ) : (
                      isValidPhoneNumber(values.phoneNumber)
                        ? values.phoneNumber
                        : "-"
                    )}
                  </div>
                </div>

                <div className="user-management-list-view-info-data-right-label-text">
                  <div className="user-management-list-view-info-data-right-label">
                    Status
                  </div>
                  <div
                    className={`user-management-list-view-info-data-right-text`}
                  >
                    {isEditing ? (
                      <div className="user-management-status-switch">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={values.status === "ACTIVE"}
                            onChange={(e) =>
                              setFieldValue(
                                "status",
                                e.target.checked ? "ACTIVE" : "INACTIVE"
                              )
                            }
                          />
                          <span className="slider" />
                          <span className="toggle-label">
                            {values.status === "ACTIVE" ? "Active" : "Inactive"}
                          </span>
                        </label>
                      </div>
                    ) : (
                      <div
                        className={`user-mngmt-status ${
                          values.status === "ACTIVE" ? "active" : "inactive"
                        }`}
                      >
                        {values.status || "-"}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="user-management-list-view-info-data-right">
                <div className="user-management-list-view-info-data-right-label-text">
                  <div
                    className={`user-management-list-view-info-data-right-label ${isEditing ? "disable-label" : ""}`}
                  >
                    First Name
                  </div>
                  <div className="user-management-list-view-info-data-right-text">
                    {isEditing ? (
                      <>
                        <div className="user-management-edit-first-name">
                          {values.first_name || "-"}
                        </div>
                      </>
                    ) : (
                      values.first_name || "-"
                    )}
                  </div>
                </div>

                <div className="user-management-list-view-info-data-right-label-text">
                  <div className="user-management-list-view-info-data-right-label">
                    Email ID
                  </div>
                  <div className="user-management-list-view-info-data-right-text">
                    {isEditing ? (
                      <>
                        <Field
                          name="email"
                          type="email"
                          placeholder="Enter email"
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="error"
                        />
                      </>
                    ) : (
                      values.email || "-"
                    )}
                  </div>
                </div>

                <div className="user-management-list-view-info-data-right-label-text">
                  <div className="user-management-list-view-info-data-right-label">
                    Role
                  </div>
                  <div className="user-management-list-view-info-data-right-text">
                    {isEditing ? (
                      <>
                        <RoleSelect
                          value={
                            typeof values.role === "object"
                              ? values.role.value
                              : values.role
                          }
                          onChange={(option) => setFieldValue("role", option)}
                          roles={roles.filter(
                            (role: any) => role.label !== "Super Admin"
                          )}
                          touched={!!touched.role}
                          error={errors.role}
                        />
                      </>
                    ) : (
                      values.role_name || "-"
                    )}
                  </div>
                </div>
                {isEditing && (
                  <div className="user-management-edit-buttons">
                    <button
                      type="button"
                      className="cancelButton"
                      onClick={() => {
                        resetForm();
                        setIsEditing(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="submitButton">
                      Save
                    </button>
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>

      {alertMessage.open && (
        <AlertComponent
          message={alertMessage.message}
          type={alertMessage.type || "info"}
          open={alertMessage.open}
          onClose={() => setAlertMessage({ open: false })}
        />
      )}
    </div>
  );
};

export default UserManagementListView;
